import axios from 'axios';
import i18n from '../i18n';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
const BACKEND_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/\/api\/v1$/, '')
  : 'http://localhost:8000';

export const getImageUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${BACKEND_URL}${path}`;
};

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (!config.params) config.params = {};
  if (!config.params.lang) {
    config.params.lang = localStorage.getItem('admin_lang') || i18n.language || 'en';
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('admin_token');
      if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(err);
  }
);

export const publicApi = {
  getHome: (lang) => api.get('/home', { params: { lang } }),
  getProducts: (params) => api.get('/products', { params }),
  getProduct: (id, lang) => api.get(`/products/${id}`, { params: { lang } }),
  getProductBySlug: (slug, lang) => api.get(`/products/slug/${slug}`, { params: { lang } }),
  getCategories: (lang) => api.get('/categories', { params: { lang } }),
  getPage: (slug, lang) => api.get(`/pages/${slug}`, { params: { lang } }),
  getCompanyInfo: () => api.get('/company-info'),
  submitInquiry: (data) => api.post('/inquiry', data),
};

export const authApi = {
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/admin/auth/logout'),
  me: () => api.get('/admin/auth/me'),
};

export const adminApi = {
  getDashboard: () => api.get('/admin/dashboard'),

  getProducts: (params) => api.get('/admin/products', { params }),
  getProduct: (id) => api.get(`/admin/products/${id}`),
  createProduct: (data) => api.post('/admin/products', data),
  updateProduct: (id, data) => api.put(`/admin/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/admin/products/${id}`),
  uploadProductImage: (id, formData) => api.post(`/admin/products/${id}/images`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteProductImage: (id, imageId) => api.delete(`/admin/products/${id}/images/${imageId}`),
  setPrimaryImage: (id, imageId) => api.patch(`/admin/products/${id}/images/${imageId}/primary`),
  uploadProductVideo: (id, formData) => api.post(`/admin/products/${id}/video`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteProductVideo: (id) => api.delete(`/admin/products/${id}/video`),

  getCategories: () => api.get('/admin/categories'),
  createCategory: (data) => api.post('/admin/categories', data),
  updateCategory: (id, data) => api.put(`/admin/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/admin/categories/${id}`),
  uploadCategoryImage: (id, formData) => api.post(`/admin/categories/${id}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),

  getInquiries: (params) => api.get('/admin/inquiries', { params }),
  getInquiryStats: () => api.get('/admin/inquiries/stats'),
  getInquiry: (id) => api.get(`/admin/inquiries/${id}`),
  updateInquiry: (id, data) => api.put(`/admin/inquiries/${id}`, data),
  deleteInquiry: (id) => api.delete(`/admin/inquiries/${id}`),

  getCompanyInfo: () => api.get('/admin/company-info'),
  updateCompanyInfo: (data) => api.put('/admin/company-info', data),
  uploadLogo: (formData) => api.post('/admin/company-info/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),

  getPages: () => api.get('/admin/pages'),
  getPage: (id) => api.get(`/admin/pages/${id}`),
  updatePage: (id, data) => api.put(`/admin/pages/${id}`, data),
  uploadPageSectionImage: (id, formData) => api.post(`/admin/pages/${id}/section-image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deletePageSectionImage: (id, sectionKey, locale = 'en') => api.delete(`/admin/pages/${id}/section-image`, { params: { section_key: sectionKey, locale } }),
  uploadPageSectionVideo: (id, formData) => api.post(`/admin/pages/${id}/section-video`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deletePageSectionVideo: (id, sectionKey, locale = 'en') => api.delete(`/admin/pages/${id}/section-video`, { params: { section_key: sectionKey, locale } }),

  getCertifications: () => api.get('/admin/certifications'),
  createCertification: (data) => api.post('/admin/certifications', data),
  updateCertification: (id, data) => api.put(`/admin/certifications/${id}`, data),
  deleteCertification: (id) => api.delete(`/admin/certifications/${id}`),
  uploadCertImage: (id, formData) => api.post(`/admin/certifications/${id}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export default api;
